<script setup>
import { useI18n } from '../composables/useI18n'
const { t } = useI18n()
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import ModeTray from './ModeTray.vue'
import { barMounted, barUnmounted, barShowsBack } from '../nav/learnerBar'

const props = defineProps({
  currentScreen: {
    type: String,
    default: 'player'
  },
  isLearning: {
    type: Boolean,
    default: false
  },
  isPlaying: {
    type: Boolean,
    default: false
  },
  isListeningMode: {
    type: Boolean,
    default: false
  },
  isPronunciationMode: {
    type: Boolean,
    default: false
  },
  showLibrary: {
    type: Boolean,
    default: false
  },
  showSettings: {
    type: Boolean,
    default: false
  },
  showSessionComplete: {
    type: Boolean,
    default: false
  },
  showCourseSelector: {
    type: Boolean,
    default: false
  },
  isAuthOpen: {
    type: Boolean,
    default: false
  },
  hasScriptPractice: {
    type: Boolean,
    default: false
  },
  isScriptPracticeMode: {
    type: Boolean,
    default: false
  },
  isPlayerReady: {
    type: Boolean,
    default: false
  },
  showListeningBtn: {
    type: Boolean,
    default: false
  },
  showPronunciationBtn: {
    type: Boolean,
    default: false
  },
  // Passed straight through to ModeTray.
  courseCode: {
    type: String,
    default: ''
  },
  // Off the player, which mode row the page lights ('talk', 'studyGroups', or
  // '' for none). null on the player, where the mode flags decide.
  pageMode: {
    type: String,
    default: null
  },
  // True when the currently playing cycle is part of a listening section
  // (LISTEN cluster, pod lap, or their bookends). Cues the skip button
  // visually so learners realise they can opt out — agency without
  // making opt-out the default.
  isInListeningCycle: {
    type: Boolean,
    default: false
  },
  // Connect (job #488): signed-in learners only — no guest identity to
  // connect with. Passed straight through to ModeTray.
  showConnect: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['navigate', 'startLearning', 'togglePlayback', 'exitListeningMode', 'backListening', 'exitPronunciationMode', 'exitScriptPracticeMode', 'toggleListening', 'togglePronunciation', 'toggleScriptPractice', 'speaking', 'talk', 'studyGroups', 'revisit', 'skip', 'openSettings', 'closeOverlays', 'closeAuth'])

// Tap feedback
const tappedItem = ref(null)
let navFeedbackTimer = null
const playButtonPressed = ref(false)
let playFeedbackTimer = null

const isOnPlayerScreen = computed(() => props.currentScreen === 'player')
// A learner page off the player — /talk, /groups, /games… (job #46, Tom
// 2026-09-24: one bottom nav on every screen). RoutedBottomNav mounts this same
// bar there with currentScreen 'page': the mode tray stays the way into a mode,
// the centre button is the way back to learning, the ‹ › pair is not shown.
const isRoutedPage = computed(() => props.currentScreen === 'page')
const hasOverlayOpen = computed(() => props.showLibrary || props.showSettings || props.showCourseSelector)

const isStopMode = computed(() =>
  props.isPlaying && !props.isListeningMode && !props.isScriptPracticeMode
)

// A mode that owns the screen — Listening, Pronunciation or Script. While one
// is up the centre button is a way back out of it, not a play button, and the
// nav never starts the main flow underneath (Tom, 2026-09-21: one mode at a time).
const hasActiveMode = computed(() =>
  props.isListeningMode || props.isPronunciationMode || props.isScriptPracticeMode
)

// The ‹ › pair steps the MAIN flow's LEGO axis. Script and Pronunciation hide
// that flow and have no step of their own, so there a tap moved the course on a
// phrase out of sight while the screen stayed put — read by a learner as a
// frozen screen (bug report e45294ad, 2026-09-23). The pair goes while either
// is up; Listening keeps it, because there it steps the overlay's own sentence.
const showsLegoSteps = computed(() =>
  isOnPlayerScreen.value && !props.isScriptPracticeMode && !props.isPronunciationMode
)

const isReturnMode = computed(() =>
  ((!isOnPlayerScreen.value || hasOverlayOpen.value || props.isAuthOpen) && !props.isPlaying) || hasActiveMode.value
)

// Tell the chrome outside the bar that it is here, and whether it is showing
// the player's back arrow (job #48: one way back, nothing over the controls).
onMounted(barMounted)
onUnmounted(() => {
  clearTimeout(navFeedbackTimer)
  clearTimeout(playFeedbackTimer)
  barUnmounted()
  if (isOnPlayerScreen.value) barShowsBack.value = false
})
watch(
  () => isOnPlayerScreen.value && isReturnMode.value,
  (back) => { if (isOnPlayerScreen.value) barShowsBack.value = back },
  { immediate: true },
)

const handleNavTap = (itemId) => {
  tappedItem.value = itemId
  clearTimeout(navFeedbackTimer)
  navFeedbackTimer = setTimeout(() => {
    navFeedbackTimer = null
    tappedItem.value = null
  }, 150)
  if (navigator.vibrate) navigator.vibrate(10)
  if (props.isListeningMode) emit('exitListeningMode')
  if (props.isPronunciationMode) emit('exitPronunciationMode')
  if (props.isScriptPracticeMode) emit('exitScriptPracticeMode')
  emit('navigate', itemId)
}

const isPlayDisabled = computed(() =>
  isOnPlayerScreen.value && !props.isPlaying && !props.isPlayerReady && !hasOverlayOpen.value && !props.isAuthOpen && !hasActiveMode.value
)

const handlePlayTap = () => {
  // A tap on the not-ready button is STILL A TAP (job #223). It used to die
  // here — no state change, no telemetry, no answer — and with cold starts
  // running p90 10.5s in production that is long enough for a learner to tap,
  // see nothing, and force-quit. Hand it to the player instead: its ready-guard
  // is the one place that decides, and it now answers the learner on screen
  // and records the refusal. The spinner stays until the player is genuinely
  // ready, so nothing here starts claiming to be something it is not.
  if (isPlayDisabled.value) {
    emit('togglePlayback')
    return
  }
  playButtonPressed.value = true
  clearTimeout(playFeedbackTimer)
  playFeedbackTimer = setTimeout(() => {
    playFeedbackTimer = null
    playButtonPressed.value = false
  }, 200)
  if (navigator.vibrate) navigator.vibrate([10, 50, 10])
  // The back arrow in a mode. Listening steps back one level inside itself
  // first (scene → scene list → pod list) and leaves only from the top — the
  // bar's arrow is the one way back (job #48); the player decides which.
  if (props.isListeningMode) {
    emit('backListening')
    return
  }
  if (props.isPronunciationMode) {
    emit('exitPronunciationMode')
    return
  }
  if (props.isScriptPracticeMode) {
    emit('exitScriptPracticeMode')
    return
  }
  // Back arrow when auth is open — close it
  if (props.isAuthOpen) {
    emit('closeAuth')
    return
  }
  // Back arrow when overlay is open — close it
  if (hasOverlayOpen.value && !props.isPlaying) {
    emit('closeOverlays')
    return
  }
  if (props.isPlaying || isOnPlayerScreen.value) {
    emit('togglePlayback')
  } else {
    emit('startLearning')
  }
}

const handleRevisit = () => {
  if (navigator.vibrate) navigator.vibrate(10)
  emit('revisit')
}

const handleSkip = () => {
  if (navigator.vibrate) navigator.vibrate(10)
  emit('skip')
}

const handleSettings = () => {
  if (navigator.vibrate) navigator.vibrate(10)
  emit('openSettings')
}
</script>

<template>
  <nav class="bottom-nav">
    <!-- Mode selector — Speaking, Listening, Talk to humans, Study groups, Script -->
    <ModeTray
      :isListeningMode="isListeningMode"
      :isPronunciationMode="isPronunciationMode"
      :courseCode="courseCode"
      :pageMode="pageMode"
      :showListeningBtn="showListeningBtn"
      :showPronunciationBtn="showPronunciationBtn"
      :hasScriptPractice="hasScriptPractice"
      :isScriptPracticeMode="isScriptPracticeMode"
      :showConnect="showConnect"
      :isVisible="!showSessionComplete && !showCourseSelector && (isOnPlayerScreen || isRoutedPage)"
      @toggleScriptPractice="emit('toggleScriptPractice')"
      @toggleListening="emit('toggleListening')"
      @togglePronunciation="emit('togglePronunciation')"
      @speaking="emit('speaking')"
      @talk="emit('talk')"
      @studyGroups="emit('studyGroups')"
    />

    <div class="nav-backdrop"></div>

    <div class="nav-content">
      <!-- Slot 1: Library -->
      <button
        class="pill-btn"
        data-walk="nav-library"
        :class="{
          active: showLibrary,
          tapped: tappedItem === 'library'
        }"
        @click="handleNavTap('library')"
        :title="t('nav.library')"
      >
        <span class="pill-btn-bg"></span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <rect x="3" y="3" width="7" height="7" rx="1.5"/>
          <rect x="14" y="3" width="7" height="7" rx="1.5"/>
          <rect x="3" y="14" width="7" height="7" rx="1.5"/>
          <rect x="14" y="14" width="7" height="7" rx="1.5"/>
        </svg>
      </button>

      <!-- Slot 2: Revisit (hidden on non-player screens and under Script / Pronunciation) -->
      <button v-show="showsLegoSteps" class="pill-btn" :class="{ tapped: tappedItem === 'revisit' }" @click="handleRevisit" :title="t('nav.previousLego')">
        <span class="pill-btn-bg"></span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>

      <!-- Slot 3: Play / Stop / Return -->
      <button
        class="center-btn"
        :class="{
          pressed: playButtonPressed,
          'is-stop': isStopMode,
          'is-return': isReturnMode,
          'is-disabled': isPlayDisabled,
        }"
        :aria-disabled="isPlayDisabled ? 'true' : 'false'"
        :aria-busy="isPlayDisabled ? 'true' : 'false'"
        :aria-label="isPlayDisabled ? 'Loading' : undefined"
        @click="handlePlayTap"
      >
        <div class="center-btn-inner">
          <svg v-if="isStopMode" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="6" width="12" height="12" rx="2"/>
          </svg>
          <svg v-else-if="isReturnMode" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
          <!-- Not ready yet: a spinner, NOT a faded play triangle. The faded
               triangle read as a tappable Play button that silently did
               nothing — the exact bad case reported on Fast 3G, where the wait
               is long enough for a learner to try it several times. A spinner
               says "working", which is the truth. -->
          <span v-else-if="isPlayDisabled" class="center-btn-spinner" aria-hidden="true"></span>
          <svg v-else viewBox="0 0 24 24" fill="currentColor">
            <polygon points="7 3 20 12 7 21 7 3"/>
          </svg>
        </div>
      </button>

      <!-- Slot 4: Skip (hidden on non-player screens and under Script / Pronunciation) -->
      <button
        v-show="showsLegoSteps"
        class="pill-btn"
        :class="{ tapped: tappedItem === 'skip', 'cue-listening': isInListeningCycle }"
        @click="handleSkip"
        :title="isInListeningCycle ? 'Skip listening' : 'Next phrase'"
        :aria-label="isInListeningCycle ? 'Skip listening section' : 'Next phrase'"
      >
        <span class="pill-btn-bg"></span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>

      <!-- Slot 5: Settings (gear) -->
      <button
        class="pill-btn"
        :class="{ active: showSettings, tapped: tappedItem === 'settings' }"
        @click="handleSettings"
        :title="t('settings.title')"
      >
        <span class="pill-btn-bg"></span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <circle cx="12" cy="12" r="3"/>
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.09a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
        </svg>
      </button>
    </div>

    <div class="safe-area-spacer"></div>
  </nav>
</template>

<style scoped>
.bottom-nav {
  position: fixed;
  /* How far the floating pill sits above the bottom of the viewport is a
     property of the SHELL, not of this component — see --shell-nav-clearance
     in styles/design-tokens.css. Web and iOS: half the safe area with a 12px
     minimum, exactly as before. Android native shell: the FULL inset plus a
     touch margin, so the row clears the system navigation bar instead of
     sitting in it. The fallback reproduces the old rule verbatim if the token
     is ever missing. */
  bottom: var(--shell-nav-clearance, max(calc(env(safe-area-inset-bottom, 0px) / 2), 12px));
  left: 50%;
  right: auto;
  transform: translateX(-50%);
  width: calc(100% - 2rem);
  max-width: 400px;
  z-index: 3000;
  overflow: visible;
}

.nav-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(10, 10, 18, 0.82);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 1.5px solid rgba(255, 255, 255, 0.35);
  border-radius: 32px;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.5),
    0 8px 24px rgba(0, 0, 0, 0.4),
    0 20px 48px rgba(0, 0, 0, 0.25);
  z-index: 100;
}

.nav-content {
  position: relative;
  z-index: 102;
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  height: 72px;
  padding: 0 10px;
}

/* Pill buttons (outer 4 slots) */
.pill-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--text-muted);
  transition: color 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  flex-shrink: 0;
}

.pill-btn svg {
  position: relative;
  z-index: 1;
  width: 24px;
  height: 24px;
}

.pill-btn:active,
.pill-btn.tapped {
  transform: scale(0.88);
}

/* Circle highlight background (Telegram-style) */
.pill-btn-bg {
  position: absolute;
  inset: 4px;
  border-radius: 50%;
  background: transparent;
  transition: background 0.2s ease;
}

.pill-btn.active .pill-btn-bg {
  background: rgba(255, 255, 255, 0.12);
}

.pill-btn.active {
  color: var(--text-primary);
}

/* Skip button cue during listening cycles — gentle pulse on the circle
   highlight to draw the eye without changing position or icon. The
   button does the same thing it always does (skip the round), this
   just makes "you can opt out of this listening section" findable. */
.pill-btn.cue-listening {
  color: rgba(255, 255, 255, 0.85);
}
.pill-btn.cue-listening .pill-btn-bg {
  background: rgba(255, 255, 255, 0.06);
  animation: skip-cue-pulse 2.4s ease-in-out infinite;
}
@keyframes skip-cue-pulse {
  0%, 100% { background: rgba(255, 255, 255, 0.04); }
  50%      { background: rgba(255, 255, 255, 0.14); }
}
@media (prefers-reduced-motion: reduce) {
  .pill-btn.cue-listening .pill-btn-bg {
    animation: none;
    background: rgba(255, 255, 255, 0.10);
  }
}

@media (hover: hover) {
  .pill-btn:hover:not(.active) .pill-btn-bg {
    background: rgba(255, 255, 255, 0.08);
  }
  .pill-btn:hover:not(.active) {
    color: rgba(255, 255, 255, 0.7);
  }
}

/* Center play/stop button */
.center-btn {
  position: relative;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(145deg, var(--ssi-red-light) 0%, var(--ssi-red) 100%);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow:
    0 4px 16px rgba(194, 58, 58, 0.4),
    0 8px 24px rgba(194, 58, 58, 0.2);
  -webkit-tap-highlight-color: transparent;
  flex-shrink: 0;
}

.center-btn:active,
.center-btn.pressed {
  transform: scale(0.9);
}

.center-btn-inner {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.center-btn-inner svg {
  width: 24px;
  height: 24px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
}

.center-btn:not(.is-stop):not(.is-disabled):not(.is-return) .center-btn-inner svg {
  margin-left: 2px;
}

.center-btn.is-disabled {
  background: linear-gradient(145deg, var(--ssi-red-light) 0%, var(--ssi-red) 100%);
  box-shadow: 0 2px 8px rgba(194, 58, 58, 0.2);
  cursor: default;
  animation: play-pulse 1.8s ease-in-out infinite;
}

.center-btn.is-disabled .center-btn-inner {
  opacity: 0.6;
}

/* The spinner must not also be dimmed by the not-ready fade — the fade is
   there to say "not tappable", the spinner is there to say "working", and at
   0.6 × the pulse the second message stops being legible. */
.center-btn.is-disabled .center-btn-inner:has(> .center-btn-spinner) {
  opacity: 1;
}

/* The loading cue itself. Sized to the icon it replaces so the button doesn't
   change shape when the real Play arrow arrives. */
.center-btn-spinner {
  display: block;
  width: 22px;
  height: 22px;
  border: 2.5px solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: center-btn-spin 0.9s linear infinite;
}

@keyframes center-btn-spin {
  to { transform: rotate(360deg); }
}

@keyframes play-pulse {
  0%, 100% { opacity: 0.45; transform: scale(1); }
  50% { opacity: 0.75; transform: scale(1.04); }
}

.center-btn.is-return {
  background: var(--bg-elevated);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.center-btn.is-return .center-btn-inner {
  color: var(--text-primary);
}

/* Safe area — not needed for floating pill (bottom offset includes safe area) */
.safe-area-spacer {
  display: none;
}

/* ═══════════════════════════════════════════════════════════════
   RESPONSIVE
   ═══════════════════════════════════════════════════════════════ */

@media (orientation: landscape) and (max-height: 500px) {
  .nav-content {
    height: 52px;
    max-width: 380px;
  }
  .pill-btn { width: 40px; height: 40px; }
  .pill-btn svg { width: 18px; height: 18px; }
  .center-btn { width: 42px; height: 42px; }
  .center-btn-inner svg { width: 18px; height: 18px; }
  .safe-area-spacer { min-height: 2px; }
}

/* ═══════════════════════════════════════════════════════════════
   MIST (LIGHT) THEME — Color overrides only
   Layout/sizing shared with cosmos via base styles above.
   ═══════════════════════════════════════════════════════════════ */

:root[data-theme="mist"] .nav-backdrop {
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1.5px solid rgba(0, 0, 0, 0.35);
  box-shadow: 0 2px 4px rgba(44, 38, 34, 0.14),
              0 8px 24px rgba(44, 38, 34, 0.10),
              0 20px 48px rgba(44, 38, 34, 0.06);
}

/* Button stroke colors on white pill — neutral, no belt tint */
:root[data-theme="mist"] .pill-btn {
  color: #6B6560;
}

:root[data-theme="mist"] .pill-btn.active .pill-btn-bg {
  background: rgba(0, 0, 0, 0.06);
}

:root[data-theme="mist"] .pill-btn.active {
  color: #2C2622;
}

/* Mist theme: skip-cue uses dark instead of white for the pulse */
:root[data-theme="mist"] .pill-btn.cue-listening {
  color: #2C2622;
}
:root[data-theme="mist"] .pill-btn.cue-listening .pill-btn-bg {
  animation-name: skip-cue-pulse-mist;
}
@keyframes skip-cue-pulse-mist {
  0%, 100% { background: rgba(0, 0, 0, 0.03); }
  50%      { background: rgba(0, 0, 0, 0.10); }
}
@media (prefers-reduced-motion: reduce) {
  :root[data-theme="mist"] .pill-btn.cue-listening .pill-btn-bg {
    animation: none;
    background: rgba(0, 0, 0, 0.07);
  }
}

@media (hover: hover) {
  :root[data-theme="mist"] .pill-btn:hover:not(.active) {
    color: #2C2622;
  }
  :root[data-theme="mist"] .pill-btn:hover:not(.active) .pill-btn-bg {
    background: rgba(0, 0, 0, 0.04);
  }
}

:root[data-theme="mist"] .center-btn.is-disabled {
  background: linear-gradient(145deg, var(--ssi-red-light) 0%, var(--ssi-red) 100%);
  box-shadow: 0 2px 4px rgba(194, 58, 58, 0.15);
}

:root[data-theme="mist"] .center-btn.is-return {
  background: rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 4px rgba(44, 38, 34, 0.10);
}

:root[data-theme="mist"] .center-btn.is-return .center-btn-inner {
  color: #2C2622;
}
</style>
